import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Flame, Trophy, Calendar, Check, ChevronRight, Sun, Moon, Clock, Trash2, Plus, Sparkles, Crown, Star, Diamond, Zap } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

const SCHEDULE_LABELS = { morning: 'Morning', night: 'Night', both: 'AM & PM' };
const SCHEDULE_ICONS = { morning: Sun, night: Moon, both: Clock };

const MILESTONE_ICONS = {
  spark: Zap, fire: Flame, crown: Crown, star: Star, diamond: Diamond, trophy: Trophy,
};

const MILESTONE_COLORS = {
  spark: 'from-amber-400 to-orange-500',
  fire: 'from-orange-400 to-red-500',
  crown: 'from-pink-400 to-rose-500',
  star: 'from-yellow-400 to-amber-500',
  diamond: 'from-cyan-400 to-blue-500',
  trophy: 'from-violet-400 to-purple-500',
};

export default function MyRoutinesPage() {
  const navigate = useNavigate();
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoutine, setExpandedRoutine] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => { fetchRoutines(); }, []);

  const fetchRoutines = async () => {
    try {
      const res = await authFetch(`${API}/routines`);
      if (res.ok) setRoutines(await res.json());
    } catch (e) {
      console.error('Error fetching routines:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckin = async (routine, productId) => {
    if (checkingIn) return;
    setCheckingIn(true);

    const todayChecked = routine.today_checkin?.products_checked || [];
    const isAlreadyChecked = todayChecked.includes(productId);
    const newChecked = isAlreadyChecked
      ? todayChecked.filter(id => id !== productId)
      : [...todayChecked, productId];

    try {
      const res = await authFetch(`${API}/routines/${routine.routine_id}/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products_checked: newChecked }),
      });

      if (res.ok) {
        const result = await res.json();

        // Update local state
        setRoutines(prev => prev.map(r =>
          r.routine_id === routine.routine_id ? {
            ...r,
            today_checkin: { ...r.today_checkin, products_checked: newChecked, completed: result.completed },
            current_streak: result.current_streak,
            longest_streak: result.longest_streak,
            total_checkins: result.total_checkins,
          } : r
        ));

        if (result.completed && !routine.today_checkin?.completed) {
          toast.success('Routine complete! Streak updated!');
        }

        if (result.new_milestone) {
          toast.success(`New milestone: ${result.new_milestone.name}!`);
        }
      }
    } catch (e) {
      toast.error('Check-in failed');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleDelete = async (routineId) => {
    if (!window.confirm('Delete this routine and all check-in history?')) return;

    try {
      const res = await authFetch(`${API}/routines/${routineId}`, { method: 'DELETE' });
      if (res.ok) {
        setRoutines(prev => prev.filter(r => r.routine_id !== routineId));
        toast.success('Routine deleted');
      }
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const totalStreakDays = routines.reduce((sum, r) => sum + (r.current_streak || 0), 0);
  const bestStreak = Math.max(0, ...routines.map(r => Math.max(r.current_streak || 0, r.longest_streak || 0)));
  const allMilestones = [];
  const seenMilestones = new Set();
  routines.forEach(r => {
    (r.milestones || []).forEach(m => {
      if (!seenMilestones.has(m.days)) {
        seenMilestones.add(m.days);
        allMilestones.push(m);
      }
    });
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="routines-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors" data-testid="routines-back-btn">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>My Routines</h1>
          <button
            onClick={() => navigate('/create-checklist')}
            className="p-2 hover:bg-primary/10 rounded-full transition-colors"
            data-testid="routines-add-btn"
          >
            <Plus className="w-6 h-6 text-primary" />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Nudge banner when there are unchecked routines */}
        {routines.length > 0 && routines.some(r => !r.today_checkin?.completed) && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="routines-nudge">
            <Flame className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-sm text-amber-800 font-medium">
              {routines.filter(r => !r.today_checkin?.completed).length === 1
                ? "1 routine left to check in today — keep the streak alive!"
                : `${routines.filter(r => !r.today_checkin?.completed).length} routines waiting for check-in today`}
            </p>
          </div>
        )}

        {/* Streak Summary Card */}
        {routines.length > 0 && (
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 to-rose-500 text-white p-5" data-testid="streak-summary">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-12 translate-x-12" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-8 -translate-x-8" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <Flame className="w-6 h-6" />
                <span className="text-sm font-bold uppercase tracking-wider opacity-80">Streak Overview</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{totalStreakDays}</p>
                  <p className="text-xs opacity-70 mt-0.5">Active Days</p>
                </div>
                <div>
                  <p className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{bestStreak}</p>
                  <p className="text-xs opacity-70 mt-0.5">Best Streak</p>
                </div>
                <div>
                  <p className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{routines.length}</p>
                  <p className="text-xs opacity-70 mt-0.5">Routines</p>
                </div>
              </div>

              {/* Milestone badges */}
              {allMilestones.length > 0 && (
                <div className="flex gap-2 mt-4 flex-wrap">
                  {allMilestones.map(m => {
                    const Icon = MILESTONE_ICONS[m.icon] || Star;
                    return (
                      <div
                        key={m.days}
                        className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full"
                        data-testid={`milestone-${m.days}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <span className="text-xs font-bold">{m.name}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {routines.length === 0 && (
          <div className="text-center py-16 space-y-4" data-testid="routines-empty">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-orange-400 to-rose-400 rounded-full flex items-center justify-center">
              <Flame className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Start a Streak
            </h2>
            <p className="text-muted-foreground max-w-xs mx-auto">
              Create a checklist and track it as a daily routine. Build streaks, earn badges, and stay consistent.
            </p>
            <button
              data-testid="routines-create-btn"
              onClick={() => navigate('/create-checklist')}
              className="btn-primary px-8 py-3 text-sm"
            >
              Create Your First Routine
            </button>
          </div>
        )}

        {/* Routine Cards */}
        {routines.map(routine => {
          const ScheduleIcon = SCHEDULE_ICONS[routine.schedule] || Clock;
          const todayChecked = routine.today_checkin?.products_checked || [];
          const totalProducts = routine.products?.length || 0;
          const checkedCount = todayChecked.length;
          const isComplete = routine.today_checkin?.completed;
          const isExpanded = expandedRoutine === routine.routine_id;
          const streakActive = routine.current_streak > 0;

          return (
            <div
              key={routine.routine_id}
              className={`rounded-2xl border-2 transition-all overflow-hidden ${
                isComplete ? 'border-green-400 bg-green-50/50' : 'border-border bg-white'
              }`}
              data-testid={`routine-card-${routine.routine_id}`}
            >
              {/* Card Header */}
              <button
                className="w-full text-left p-4 flex items-center gap-3"
                onClick={() => setExpandedRoutine(isExpanded ? null : routine.routine_id)}
                data-testid={`routine-toggle-${routine.routine_id}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                  isComplete
                    ? 'bg-green-500 text-white'
                    : streakActive
                      ? 'bg-gradient-to-br from-orange-400 to-rose-400 text-white'
                      : 'bg-gray-100 text-gray-400'
                }`}>
                  {isComplete ? <Check className="w-6 h-6" /> : <ScheduleIcon className="w-6 h-6" />}
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-base truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {routine.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-muted-foreground">{SCHEDULE_LABELS[routine.schedule]}</span>
                    <span className="text-xs text-muted-foreground">&middot;</span>
                    <span className="text-xs text-muted-foreground">{checkedCount}/{totalProducts} today</span>
                  </div>
                </div>

                {/* Streak badge */}
                {streakActive && (
                  <div className="flex items-center gap-1 bg-orange-100 text-orange-600 px-2.5 py-1 rounded-full shrink-0">
                    <Flame className="w-4 h-4" />
                    <span className="text-sm font-black">{routine.current_streak}</span>
                  </div>
                )}

                <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              </button>

              {/* Progress bar */}
              <div className="px-4 pb-2">
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : 'bg-primary'}`}
                    style={{ width: `${totalProducts ? (checkedCount / totalProducts) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Expanded: Product checklist */}
              {isExpanded && (
                <div className="border-t border-border/50 px-4 py-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
                  {routine.product_details?.map(product => {
                    const isChecked = todayChecked.includes(product.product_id);
                    return (
                      <button
                        key={product.product_id}
                        data-testid={`checkin-product-${product.product_id}`}
                        onClick={() => handleCheckin(routine, product.product_id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
                          isChecked ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border-2 transition-all ${
                          isChecked ? 'bg-green-500 border-green-500' : 'border-gray-300'
                        }`}>
                          {isChecked && <Check className="w-4 h-4 text-white" />}
                        </div>
                        {product.image ? (
                          <img src={product.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : null}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold truncate ${isChecked ? 'line-through text-muted-foreground' : ''}`}>
                            {product.name}
                          </p>
                          <p className="text-xs text-muted-foreground">{product.brand}</p>
                        </div>
                      </button>
                    );
                  })}

                  {/* Routine actions */}
                  <div className="flex items-center justify-between pt-2 border-t border-border/50">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {routine.total_checkins} total
                      </span>
                      <span className="flex items-center gap-1">
                        <Trophy className="w-3.5 h-3.5" />
                        Best: {routine.longest_streak}
                      </span>
                    </div>
                    <button
                      data-testid={`delete-routine-${routine.routine_id}`}
                      onClick={(e) => { e.stopPropagation(); handleDelete(routine.routine_id); }}
                      className="p-2 hover:bg-red-50 rounded-full text-muted-foreground hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Milestone badges for this routine */}
                  {routine.milestones?.length > 0 && (
                    <div className="flex gap-2 flex-wrap pt-1">
                      {routine.milestones.map(m => {
                        const Icon = MILESTONE_ICONS[m.icon] || Star;
                        const gradient = MILESTONE_COLORS[m.icon] || 'from-gray-400 to-gray-500';
                        return (
                          <div
                            key={m.days}
                            className={`flex items-center gap-1.5 bg-gradient-to-r ${gradient} text-white px-2.5 py-1 rounded-full text-xs font-bold`}
                          >
                            <Icon className="w-3 h-3" />
                            {m.name}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
